// Landing / splash homepage (Checkpoint 5.04 — rebuilt from the approved mock; was the bare 5.01 login
// splash). This IS the signed-out state: a content-rich landing page that DESCRIBES the app (required for
// Google OAuth branding verification — the homepage must say what PrettyGolf is), over the splashbg.jpg
// hero image. Layout = centred hero (title + old-school/mobile tagline), a two-column pitch/sign-in row
// that concertinas to a vertical stack on mobile (flex-wrap), and a company + legal footer.
//
// Routing is NOT owned here (unchanged from 5.01): the buttons call the REAL auth SDK (googleSignIn /
// emailSignIn / emailSignUp from auth.js); on success Firebase's onAuthStateChanged fires in boot.js,
// which hides this splash and boots the game. So the splash only collects input, calls auth, shows a busy
// state, and surfaces friendly errors. Signed-in users never see this — boot.js sends them straight to the
// game (session persistence from 1a unchanged).
import { googleSignIn, emailSignIn, emailSignUp, friendlyAuthError, isCancellation } from './auth.js';
import { logoUrl } from './brand.js';

// Hero background — resolved via the project's standard glob→urls[key] pattern (the 4.14.2 lesson: use the
// hashed URL VALUE, never the source-path key). 6.0 — splashbg.jpg moved to static/brand/ when the rest of
// substrates; eager glob emits it as a bundled, same-origin asset (CSP-safe for a CSS url()).
const BG_URL = (() => {
  const urls = import.meta.glob('../static/brand/*.jpg', { eager: true, query: '?url', import: 'default' });
  return urls['../static/brand/splashbg.jpg'] || '';
})();

// The wordmark. Falls back to the text title when absent, so a missing asset never leaves the landing page
// nameless (which would also break the Google OAuth branding check this page exists to satisfy).
const LOGO_URL = logoUrl();

const STYLE_ID = 'pg-splash-style';
const CSS = `
#splash{position:fixed;inset:0;z-index:60;display:none;flex-direction:column;align-items:center;
  padding:calc(env(safe-area-inset-top) + 30px) 22px calc(env(safe-area-inset-bottom) + 24px);
  color:#e9f0e3;font-family:ui-monospace,"SF Mono",Menlo,Consolas,monospace;overflow-y:auto;
  background:#0e130d center center / cover no-repeat;}
#splash.show{display:flex;animation:spfade .28s ease;}
@keyframes spfade{from{opacity:0} to{opacity:1}}
/* scrim — a soft top-and-bottom darkening over the hero image so the hero + left-column pitch text stay
   crisp (paired with per-line text-shadows) without over-darkening the art in the middle. position:fixed so
   it covers the viewport and doesn't scroll away on tall/portrait layouts. Tuned generically against the
   supplied splashbg.jpg — adjust the stops here if the art changes. */
#splash::before{content:"";position:fixed;inset:0;z-index:0;pointer-events:none;
  background:linear-gradient(180deg,rgba(6,10,6,.60) 0%,rgba(6,10,6,.30) 26%,rgba(6,10,6,.34) 62%,rgba(6,10,6,.74) 100%);}

/* content column — centres vertically when it fits (margin:auto in the column flex), scrolls when it doesn't. */
#splash .sp-inner{position:relative;z-index:1;width:100%;max-width:1040px;margin:auto;
  display:flex;flex-direction:column;align-items:center;gap:26px;}

#splash .sp-hero{display:flex;flex-direction:column;align-items:center;gap:10px;text-align:center;}
/* The wordmark (5.37). A transparent PNG sitting DIRECTLY on the hero image + scrim — no plate, no card.
   Width-driven with height:auto, so the 959x219 art keeps its aspect at every width; a drop-shadow (not a
   backing) buys it legibility over the photograph, matching how .sp-title's text-shadow used to. */
#splash .sp-logo{display:block;width:min(380px,74vw);height:auto;
  filter:drop-shadow(0 3px 14px rgba(0,0,0,.75));}
#splash .sp-title{font-size:clamp(38px,8vw,64px);font-weight:700;letter-spacing:.01em;line-height:1;
  color:#f5f8f0;text-shadow:0 3px 14px rgba(0,0,0,.7);}
#splash .sp-title .sp-accent{color:#ffd268;}
#splash .sp-tag{max-width:640px;font-size:clamp(13px,2.2vw,17px);line-height:1.45;color:#dbe6d1;
  text-shadow:0 1px 6px rgba(0,0,0,.8);}

/* the two-column concertina: side-by-side on desktop, stacked (pitch above, sign-in below) on mobile. */
#splash .sp-cols{width:100%;display:flex;flex-wrap:wrap;gap:24px;align-items:stretch;justify-content:center;}
#splash .sp-pitch{flex:1 1 300px;min-width:0;display:flex;flex-direction:column;justify-content:center;gap:20px;}
#splash .sp-beat{font-size:clamp(14px,1.6vw,15.5px);line-height:1.55;color:#eef4e8;
  text-shadow:0 1px 5px rgba(0,0,0,.85);padding-left:16px;border-left:2px solid rgba(255,210,104,.55);}
#splash .sp-beat b{color:#ffe1a0;font-weight:600;}

/* sign-in panel — its own dark backing so it's legible over the image regardless of the scrim. */
#splash .sp-card{flex:1 1 300px;max-width:390px;align-self:center;display:flex;flex-direction:column;gap:12px;
  background:rgba(10,14,9,.82);border:1px solid rgba(205,216,200,.2);border-radius:16px;padding:22px;
  backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);box-shadow:0 12px 48px rgba(0,0,0,.55);}
#splash .sp-card-h{font-size:18px;font-weight:700;color:#f5f8f0;letter-spacing:.01em;}
#splash .sp-card-sub{font-size:12.5px;line-height:1.45;color:#a9b8a0;margin-bottom:2px;}
#splash .sp-btn{display:flex;align-items:center;justify-content:center;gap:10px;width:100%;
  padding:12px 14px;border-radius:12px;font:inherit;font-size:14px;letter-spacing:.04em;cursor:pointer;
  border:1px solid rgba(205,216,200,.2);background:rgba(205,216,200,.06);color:#e6ede0;
  transition:background .15s ease,opacity .15s ease;}
#splash .sp-btn:hover{background:rgba(205,216,200,.12);}
#splash .sp-btn:disabled{opacity:.5;cursor:default;}
#splash .sp-btn.sp-google{background:#f6f7f3;color:#20261c;border-color:#f6f7f3;font-weight:600;}
#splash .sp-btn.sp-google:hover{background:#fff;}
#splash .sp-btn.sp-primary{background:#ffd268;color:#10160f;border-color:#ffd268;font-weight:700;
  text-transform:uppercase;letter-spacing:.07em;}
#splash .sp-btn.sp-primary:hover{background:#ffdd85;}
#splash .sp-gicon{width:17px;height:17px;flex:0 0 auto;}
#splash .sp-or{display:flex;align-items:center;gap:10px;color:#8a9a82;font-size:10px;
  letter-spacing:.18em;text-transform:uppercase;}
#splash .sp-or::before,#splash .sp-or::after{content:"";flex:1;height:1px;background:rgba(205,216,200,.16);}
#splash .sp-field{display:flex;flex-direction:column;gap:5px;}
#splash .sp-field label{font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:#9fb094;}
#splash .sp-field input{width:100%;padding:11px 12px;border-radius:10px;font:inherit;font-size:14px;
  color:#eef3e8;background:rgba(6,10,6,.7);border:1px solid rgba(205,216,200,.2);}
#splash .sp-field input:focus{outline:none;border-color:rgba(255,210,104,.7);
  box-shadow:0 0 0 2px rgba(255,210,104,.14);}
#splash .sp-msg{min-height:16px;font-size:12px;line-height:1.35;letter-spacing:.01em;color:#f2a97a;
  text-align:center;}
#splash .sp-msg.sp-ok{color:#9fd08a;}
#splash .sp-toggle{font-size:12px;text-align:center;color:#a9b8a0;}
#splash .sp-toggle button{font:inherit;font-size:12px;color:#ffd268;background:none;border:none;
  cursor:pointer;text-decoration:underline;padding:0 2px;}
#splash .sp-seam{width:100%;}  /* 1c guest/trial button mounts here */

/* footer — company details + real legal links, on the page (Google branding requirement). */
#splash .sp-foot{max-width:760px;font-size:11px;letter-spacing:.02em;line-height:1.8;color:#b7c4ad;
  text-align:center;text-shadow:0 1px 4px rgba(0,0,0,.85);}
#splash .sp-foot a{color:#d7e2ce;text-decoration:underline;text-underline-offset:2px;}
#splash .sp-foot a:hover{color:#fff;}
`;

function injectStyle() {
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = CSS;
  document.head.appendChild(el);
}

// Google 'G' as an inline SVG (data-URI-free, CSP-safe, no network) so the button reads as the real
// Google control without a bundled asset.
const GOOGLE_SVG = `<svg class="sp-gicon" viewBox="0 0 48 48" aria-hidden="true"><path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/><path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/><path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"/><path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/></svg>`;

let root = null;
// Default to SIGN-UP: the primary CTA is "Sign up & play free" (the mock's main action); the toggle drops to
// sign-in for returning players.
let mode = 'signup';   // 'signup' | 'signin'
let busy = false;

function build() {
  injectStyle();
  root = document.createElement('div');
  root.id = 'splash';
  // The hero image is a hashed asset URL → set inline (CSS can't template it). Fallback colour stays if absent.
  if (BG_URL) root.style.backgroundImage = `url("${BG_URL}")`;
  root.innerHTML = `
    <div class="sp-inner">
      <div class="sp-hero">
        ${LOGO_URL
          ? `<img class="sp-logo" src="${LOGO_URL}" alt="PrettyGolf" draggable="false">`
          : '<div class="sp-title">Pretty<span class="sp-accent">Golf</span></div>'}
        <div class="sp-tag">A golf game of the old school — brought up to date, and playable on desktop and mobile.</div>
      </div>

      <div class="sp-cols">
        <div class="sp-pitch">
          <div class="sp-beat">Beautiful, hand-crafted golf courses set in gorgeous corners of the world — the depth of the classics, in your browser, on any screen.</div>
          <div class="sp-beat">Follow the <b>Magellan Edition World Tour</b>: start in the mountains, play your way to par, and unlock exotic new destinations one course at a time.</div>
          <div class="sp-beat">An honest golf engine underneath — shape your shots, read the wind, and master real ball flight. What you see is what you play.</div>
        </div>

        <div class="sp-card">
          <div class="sp-card-h">Play free</div>
          <div class="sp-card-sub">Sign in to start the Magellan Edition World Tour.</div>
          <button type="button" class="sp-btn sp-google" data-act="google">${GOOGLE_SVG}<span>Continue with Google</span></button>
          <div class="sp-or">or</div>
          <form class="sp-form" novalidate>
            <div class="sp-field">
              <label for="sp-email">Email</label>
              <input id="sp-email" type="email" autocomplete="email" inputmode="email" placeholder="you@example.com" />
            </div>
            <div class="sp-field" style="margin-top:10px">
              <label for="sp-pw">Password</label>
              <input id="sp-pw" type="password" autocomplete="new-password" placeholder="••••••••" />
            </div>
            <div class="sp-msg" data-role="msg" aria-live="polite"></div>
            <button type="submit" class="sp-btn sp-primary" data-role="primary">Sign up &amp; play free</button>
          </form>
          <div class="sp-toggle" data-role="toggle"></div>
          <!-- 1c SEAM: "Continue as guest → trial hole" (anonymous auth) mounts into .sp-seam. Empty for now. -->
          <div class="sp-seam" data-role="seam"></div>
        </div>
      </div>

      <div class="sp-foot">
        &copy; Otterspool Studios Ltd, trading as Otterspool Labs &middot; Company 07716481 &middot; 20&ndash;22 Wenlock Road, London N1 7GU<br>
        <a href="/privacy.html" target="_blank" rel="noopener">Privacy Policy</a> &middot;
        <a href="/terms.html" target="_blank" rel="noopener">Terms of Service</a> &middot;
        <a href="mailto:team@otterspoollabs.com">team@otterspoollabs.com</a>
      </div>
    </div>
  `;
  document.body.appendChild(root);

  const q = (sel) => root.querySelector(sel);
  const email = q('#sp-email');
  const pw = q('#sp-pw');
  const msg = q('[data-role="msg"]');
  const primary = q('[data-role="primary"]');
  const toggle = q('[data-role="toggle"]');
  const googleBtn = q('[data-act="google"]');
  const form = q('.sp-form');

  const setBusy = (on) => {
    busy = on;
    root.querySelectorAll('button,input').forEach((el) => { el.disabled = on; });
  };
  const showMsg = (text, ok = false) => {
    msg.textContent = text || '';
    msg.classList.toggle('sp-ok', !!ok);
  };
  const renderMode = () => {
    primary.textContent = mode === 'signup' ? 'Sign up & play free' : 'Sign in';
    pw.autocomplete = mode === 'signup' ? 'new-password' : 'current-password';
    toggle.innerHTML = mode === 'signup'
      ? 'Already have an account? <button type="button" data-act="toggle">Sign in</button>'
      : 'New here? <button type="button" data-act="toggle">Create an account</button>';
    toggle.querySelector('[data-act="toggle"]').addEventListener('click', () => {
      mode = mode === 'signup' ? 'signin' : 'signup';
      showMsg('');
      renderMode();
    });
  };
  renderMode();

  // On a caught auth failure: cancellations stay silent (user backed out), everything else shows
  // a friendly line. Never surface a raw `auth/...` code.
  const handleError = (err) => {
    setBusy(false);
    if (isCancellation(err)) { showMsg(''); return; }
    showMsg(friendlyAuthError(err));
  };

  googleBtn.addEventListener('click', () => {
    if (busy) return;
    showMsg('');
    setBusy(true);
    // On success onAuthStateChanged (boot.js) hides the splash + boots the game — no local success path.
    googleSignIn().catch(handleError);
  });

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (busy) return;
    const em = email.value.trim();
    const pass = pw.value;
    if (!em) { showMsg('Please enter your email address.'); return; }
    if (!pass) { showMsg('Please enter your password.'); return; }
    showMsg('');
    setBusy(true);
    const p = mode === 'signup' ? emailSignUp(em, pass) : emailSignIn(em, pass);
    p.catch(handleError);
  });
}

export function showSplash() {
  if (!root) build();
  // reset transient state each time it's shown (e.g. returning after a sign-out reload is a fresh page,
  // but this keeps it correct if ever shown without a reload)
  busy = false;
  root.querySelectorAll('button,input').forEach((el) => { el.disabled = false; });
  const msg = root.querySelector('[data-role="msg"]');
  if (msg) { msg.textContent = ''; msg.classList.remove('sp-ok'); }
  root.classList.add('show');
}

export function hideSplash() {
  if (root) root.classList.remove('show');
}
